import { ConfigType } from '../config'

export type FetcherResponse<T = any, E = any> = {
  ok: boolean
  status: number
  data: T
  error: E
}

const withQueryString = (url: RequestInfo, params?: any) => {
  if (!params) return url

  const keys = Object.keys(params)
  if (keys.length <= 0) return url

  const urlParams = keys
    .filter((key) => params[key] !== undefined)
    .map((key) => key + '=' + params[key])
    .join('&')
  if (urlParams) return `${url}?${urlParams}`

  return `${url}`
}

async function jsonParser<T = any>(
  resp: Response
): Promise<FetcherResponse<T>> {
  let data: T

  try {
    data = (await resp.json()) as T
  } catch {
    data = undefined as never
  }

  return { ok: resp.ok, status: resp.status, data, error: undefined as never }
}

function errorParser<T = any, E = any>(e: any): FetcherResponse<T, E> {
  return {
    ok: false,
    status: 500,
    data: undefined as never,
    error: e.message as E
  }
}

function request<T = any, E = any>(
  url: RequestInfo,
  options?: RequestInit,
  config?: any
) {
  const { token, business_uid } = config
  const headers = options?.headers || {}

  if (token) headers['Authorization'] = `Jwt ${token}`
  if (business_uid) headers['X-Service-Key'] = business_uid

  const requestOptions: any = {
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...options?.headers,
      ...headers
    }
  }

  if (config?.apiKey) {
    requestOptions.headers['X-API-Key'] = config.apiKey
  }

  if (requestOptions.body) {
    if (requestOptions.body instanceof FormData) {
      delete requestOptions.headers['Content-Type']
    } else if (requestOptions.headers['Content-Type'] === 'application/json') {
      requestOptions.body = JSON.stringify(requestOptions.body)
    }
  }

  return fetch(`${config.apiUrl}/${url}`, requestOptions)
    .then((...props) => jsonParser<T>(...props))
    .catch((e) => errorParser<T, E>(e))
}

function fetcherWithCookiesParams<T = any, E = any>(
  url: RequestInfo,
  params?: any,
  config?: any
) {
  return {
    get: (getParams?: any, options?: RequestInit) =>
      request<T, E>(
        withQueryString(url, { ...getParams, ...params }),
        { ...options, method: 'GET' },
        config
      ),
    post: (body?: any, options?: RequestInit) =>
      request<T, E>(
        withQueryString(url, params),
        { ...options, body, method: 'POST' },
        config
      ),
    put: (body?: any, options?: RequestInit) =>
      request<T, E>(
        withQueryString(url, params),
        { ...options, body, method: 'PUT' },
        config
      ),
    patch: (body?: any, options?: RequestInit) =>
      request<T, E>(
        withQueryString(url, params),
        { ...options, body, method: 'PATCH' },
        config
      ),
    delete: (options: RequestInit) => () =>
      request<T, E>(
        withQueryString(url, params),
        { ...options, method: 'DELETE' },
        config
      )
  }
}

export default function fetcher<T = any, E = any>(
  url: RequestInfo,
  options?: { params: any; config?: ConfigType }
) {
  const setConfig = (config: any) =>
    fetcher<T, E>(url, {
      params: options?.params,
      config
    })
  const setParams = (params: any) =>
    fetcher<T, E>(url, {
      params,
      config: options?.config
    })

  return {
    setConfig,
    setParams,
    ...fetcherWithCookiesParams<T, E>(
      url,
      {
        visitor: options?.config?.visitor_id,
        ...options?.params
      },
      options?.config
    )
  }
}

export const simpleFetcher = (url: string, options?: any) =>
  request(url, options).then(({ data }) => data)
